import { clamp, useWizardContext } from '@lounge-fe/ui-kit'
import { useFittingRoomContext } from '../FittingRoomProvider'
import { FittingRoomValues } from '../types'

export const useLoungeFit = () => {
  const { values } = useWizardContext<FittingRoomValues>()

  const {
    braSizes,
    bandSizes,
    cupSizes,
    bottomsSizes,
    bandFitModifier,
    cupFitModifier,
    hookFitModifier,
    strapFitModifier,
    maxBandSizeModifier,
    maxCupSizeModifier,
    maxBottomsSizeModifier,
    competitors,
  } = useFittingRoomContext()

  const getCurrentBrandModifier = (
    sizeKey: keyof (typeof competitors)[number],
  ): number => {
    const { currentBrand } = values
    const modifier = competitors.find(({ name }) => name === currentBrand)?.[
      sizeKey
    ]
    return Number(modifier ?? 0)
  }

  const calculateBandSize = (): number | undefined => {
    const currentBandSize = Number(values.bandSize)
    const currentBandSizeIndex = bandSizes.findIndex(
      (bandSize) => bandSize === currentBandSize,
    )

    const modifier =
      bandFitModifier[values.bandFit ?? 'comfortable'] +
      strapFitModifier[values.strapFit ?? 'comfortable'] +
      hookFitModifier[values.hookFit ?? 'comfortable'] +
      getCurrentBrandModifier('bandSizeModifier')

    const clampedModifier = clamp(
      modifier,
      -maxBandSizeModifier,
      maxBandSizeModifier,
    )

    return bandSizes[currentBandSizeIndex + clampedModifier]
  }

  const calculateCupSize = (): string | undefined => {
    const currentCupSizeIndex = cupSizes.findIndex(
      (cupSize) => cupSize === values.cupSize,
    )

    const modifier =
      cupFitModifier[values.cupFit ?? 'comfortable'] +
      getCurrentBrandModifier('cupSizeModifier')

    const clampedModifier = clamp(
      modifier,
      -maxCupSizeModifier,
      maxCupSizeModifier,
    )
    return cupSizes[currentCupSizeIndex + clampedModifier]
  }

  const calculateBottomsSize = (): string | undefined => {
    const currentBottomsSizeIndex = bottomsSizes.findIndex(
      (bottomsSize) => bottomsSize === values.bottomsSize,
    )
    const modifier = getCurrentBrandModifier('bottomsSizeModifier')

    const clampedModifier = clamp(
      modifier,
      -maxBottomsSizeModifier,
      maxBottomsSizeModifier,
    )

    return bottomsSizes[currentBottomsSizeIndex + clampedModifier]
  }

  const getLoungeBraSize = (
    bandSize: number | undefined,
    cupSize: string | undefined,
  ) => {
    if (!bandSize || !cupSize) return

    return braSizes.find(({ band_size, cup_size }) => {
      return Number(band_size) === bandSize && cup_size === cupSize
    })
  }

  const calculate = () => {
    const bandSize = calculateBandSize()
    const cupSize = calculateCupSize()
    const braSize = getLoungeBraSize(bandSize, cupSize)

    return {
      bandSize: calculateBandSize(),
      cupSize: calculateCupSize(),
      braletteSize: braSize ? braSize.bralette_size : undefined,
      bottomsSize: calculateBottomsSize(),
      inStock: !!braSize,
    }
  }

  return calculate
}
